/**
 * useDevice — React hook surfacing the three-layer device profile
 * defined in lib/device-profile.ts. See Combined Addendum §7 for spec.
 *
 * Re-runs detection on resize and orientationchange so layout/form-factor
 * downstream consumers stay accurate. Platform/capability values are
 * stable across the session; we still re-derive them so debugging tools
 * that override UA hints reflect immediately.
 */

import { useEffect, useState } from 'react';
import { detectDevice, applyDeviceAttributes, type DeviceProfile } from './device-profile';

let cached: DeviceProfile | null = null;

/**
 * Idempotent — first call applies the data attributes to <html> so CSS
 * platform selectors light up before React commits the first render.
 */
export function ensureDeviceAttributes(): DeviceProfile {
  if (cached) return cached;
  cached = detectDevice();
  applyDeviceAttributes(cached);
  return cached;
}

export function useDevice(): DeviceProfile {
  const [profile, setProfile] = useState<DeviceProfile>(() => ensureDeviceAttributes());

  useEffect(() => {
    const reapply = () => {
      const next = detectDevice();
      cached = next;
      applyDeviceAttributes(next);
      setProfile(next);
    };
    window.addEventListener('resize', reapply);
    window.addEventListener('orientationchange', reapply);
    // Color-scheme + reduced-motion swing under user control; keep in sync.
    const dark = window.matchMedia('(prefers-color-scheme: dark)');
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    dark.addEventListener?.('change', reapply);
    motion.addEventListener?.('change', reapply);
    return () => {
      window.removeEventListener('resize', reapply);
      window.removeEventListener('orientationchange', reapply);
      dark.removeEventListener?.('change', reapply);
      motion.removeEventListener?.('change', reapply);
    };
  }, []);

  return profile;
}
